import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { FormButton } from "@/components/ui/FormButton";
import { toBlob } from "html-to-image";
import { Speedtest, type SpeedtestData } from "@/services/speedtest/Speedtest";
import {
    type CheckResult,
    type CheckStatus,
    checkBrowser,
    checkCookies,
    checkMemory,
    checkOperatingSystem,
    checkWebcam,
    evaluateNetwork,
    splitClientIp
} from "@/services/speedtest/systemCheck";

const SPEEDTEST_BACKEND = "https://speedtest.ecpi.net/backend/";

const StatusBadge: React.FC<{ status: CheckStatus; pending?: boolean }> = ({ status, pending }) =>
{
    if (pending) return <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" aria-label="Testing" />;
    switch (status)
    {
        case "Pass":
            return <span className="inline-flex items-center gap-1 text-green-700 text-[11px] font-semibold"><CheckCircle2 className="w-3.5 h-3.5" />Pass</span>;
        case "Alert":
            return <span className="inline-flex items-center gap-1 text-(--ecpi-yellow-text-hex) text-[11px] font-semibold"><AlertTriangle className="w-3.5 h-3.5" />Alert</span>;
        case "Fail":
            return <span className="inline-flex items-center gap-1 text-red-600 text-[11px] font-semibold"><XCircle className="w-3.5 h-3.5" />Fail</span>;
        default:
            return null;
    }
};

const ResultRow: React.FC<{ result: CheckResult; pending?: boolean }> = ({ result, pending }) =>
{
    if (result.hidden) return null;
    return (
        <div className="py-1 border-b border-gray-100 last:border-b-0">
            <div className="flex items-center justify-between gap-2">
                <div className="text-gray-600 text-xs font-sora w-[42%] shrink-0">{result.label}</div>
                <div className="text-gray-800 text-xs font-semibold font-sora flex-1 min-w-0 truncate" title={result.value}>{result.value || "\u00A0"}</div>
                <div className="w-[52px] shrink-0 flex justify-end"><StatusBadge status={result.status} pending={pending} /></div>
            </div>
            {result.messages && result.messages.length > 0 && (
                <div className="text-[11px] font-sora text-gray-500 leading-4">
                    {result.messages.join(" ")}
                    {result.infoUrl && (
                        <> <a href={result.infoUrl} target="_blank" rel="noreferrer" className="text-[#0053C6] underline whitespace-nowrap">More Info</a></>
                    )}
                </div>
            )}
        </div>
    );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white rounded-lg outline outline-1 outline-offset-[-1px] outline-gray-200 px-3 py-2">
        <div className="text-gray-800 text-sm font-semibold font-sora mb-0.5">{title}</div>
        {children}
    </div>
);

export interface SystemCheckOutcome
{
    screenshot: File;
    meetsRequirements: boolean;
}

interface SystemCheckPanelProps
{
    // Fired with a PNG of the results once every check has finished
    onComplete?: (outcome: SystemCheckOutcome) => void;
    // Fired when a re-run starts, so any previous outcome can be discarded
    onReset?: () => void;
    // Fired whenever the panel starts/stops being busy (network test running or results image being captured)
    onBusyChange?: (busy: boolean) => void;
}

const SystemCheckPanel: React.FC<SystemCheckPanelProps> = ({ onComplete, onReset, onBusyChange }) =>
{
    const [browserResults, setBrowserResults] = useState<CheckResult[]>([]);
    const [systemResults, setSystemResults] = useState<CheckResult[]>([]);
    const [webcamResult, setWebcamResult] = useState<CheckResult | null>(null);

    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [live, setLive] = useState<SpeedtestData | null>(null);
    const [networkResults, setNetworkResults] = useState<CheckResult[] | null>(null);
    const [networkError, setNetworkError] = useState<string | null>(null);

    const testRef = useRef<Speedtest | null>(null);
    const watchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pingSamples = useRef<number[]>([]);
    const jitterSamples = useRef<number[]>([]);

    const captureRef = useRef<HTMLDivElement | null>(null);
    const callbacksRef = useRef({ onComplete, onReset, onBusyChange });
    callbacksRef.current = { onComplete, onReset, onBusyChange };
    const [capturing, setCapturing] = useState(false);

    useEffect(() =>
    {
        callbacksRef.current.onBusyChange?.(running || capturing);
    }, [running, capturing]);

    const runNetworkTest = useCallback(() =>
    {
        callbacksRef.current.onReset?.();
        testRef.current?.dispose();
        pingSamples.current = [];
        jitterSamples.current = [];
        setNetworkResults(null);
        setNetworkError(null);
        setLive(null);
        setProgress(0);
        setRunning(true);

        const failNetworkTest = (message: string) =>
        {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            testRef.current?.dispose();
            testRef.current = null;
            setRunning(false);
            setLive(null);
            setProgress(0);
            setNetworkError(message);
        };
        const resetWatchdog = () =>
        {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            watchdogRef.current = setTimeout(
                () => failNetworkTest("The network test stopped responding. Please check your connection and try again."),
                30000);
        };
        resetWatchdog();

        const s = new Speedtest();
        s.setParameter("url_dl", SPEEDTEST_BACKEND + "garbage.php");
        s.setParameter("url_ul", SPEEDTEST_BACKEND + "empty.php");
        s.setParameter("url_ping", SPEEDTEST_BACKEND + "empty.php");
        s.setParameter("url_getIp", SPEEDTEST_BACKEND + "getIP.php");
        // Cross-origin backend: makes the worker append cors=true so the server returns CORS headers
        s.setParameter("mpot", true);

        let lastData: SpeedtestData | null = null;
        let lastProgressKey = "";
        // Speedtest already logs the underlying worker error (console.error). The "Run Network Test Again"
        // button below re-runs the test, so point there rather than at a page refresh.
        s.onerror = () =>
        {
            failNetworkTest("The network test could not be started. Please select \"Run Network Test Again\" below. If it keeps failing, check your connection or try Google Chrome.");
        };
        s.onupdate = (data) =>
        {
            lastData = data;
            const progressKey = `${data.testState}|${data.dlProgress}|${data.ulProgress}|${data.pingProgress}|${data.clientIp}`;
            if (progressKey !== lastProgressKey)
            {
                lastProgressKey = progressKey;
                resetWatchdog();
            }
            if (data.testState === 2 && Number(data.pingStatus) > 0)
            {
                pingSamples.current.push(Number(data.pingStatus));
                jitterSamples.current.push(Number(data.jitterStatus));
            }
            setLive(data);
            setProgress((Number(data.dlProgress) * 2 + Number(data.ulProgress) * 2 + Number(data.pingProgress)) / 5);
        };
        s.onend = (aborted) =>
        {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            setRunning(false);
            testRef.current = null;
            if (aborted || !lastData)
            {
                setLive(null);
                setProgress(0);
                return;
            }
            setNetworkResults(evaluateNetwork({
                clientIp: lastData.clientIp,
                download: lastData.dlStatus,
                upload: lastData.ulStatus,
                pingSamples: pingSamples.current,
                jitterSamples: jitterSamples.current
            }));
        };
        testRef.current = s;
        s.start();
    }, []);

    const abortTest = () =>
    {
        testRef.current?.abort();
    };

    useEffect(() =>
    {
        setBrowserResults([...checkBrowser(), checkCookies()]);
        setSystemResults([...checkOperatingSystem(), checkMemory()]);
        checkWebcam().then(setWebcamResult);
        runNetworkTest();

        return () =>
        {
            if (watchdogRef.current) clearTimeout(watchdogRef.current);
            testRef.current?.dispose();
        };
    }, [runNetworkTest]);

    // Once every check has a final result, render the results area to a PNG and hand it to the parent
    useEffect(() =>
    {
        if (!networkResults || !webcamResult || !captureRef.current) return;

        const allResults = [...browserResults, webcamResult, ...systemResults, ...networkResults].filter(r => !r.hidden);
        const meetsRequirements = !allResults.some(r => r.status === "Fail");

        let cancelled = false;
        setCapturing(true);
        toBlob(captureRef.current, {
            backgroundColor: "#ffffff",
            pixelRatio: 2,
            filter: (node) => !(node instanceof HTMLElement && node.dataset.captureExclude === "true")
        })
            .then((blob) =>
            {
                if (cancelled || !blob) return;
                const stamp = new Date().toISOString().replace(/[:.]/g, "-");
                const screenshot = new File([blob], `SystemCheckResults_${stamp}.png`, { type: "image/png" });
                callbacksRef.current.onComplete?.({ screenshot, meetsRequirements });
            })
            .catch((err) =>
            {
                console.error("Failed to capture system check results", err);
                if (!cancelled) setNetworkError("We couldn't save an image of your results. Please run the network test again.");
            })
            .finally(() =>
            {
                if (!cancelled) setCapturing(false);
            });

        return () => { cancelled = true; };
    }, [networkResults, webcamResult, browserResults, systemResults]);

    const liveNetworkRows = (): CheckResult[] =>
    {
        const { ip, isp } = splitClientIp(live?.clientIp ?? "");
        const show = (v: string | undefined, active: boolean) => (active && Number(v) === 0 ? "..." : v ?? "");
        return [
            { key: "ip", label: "IP Address", value: ip || "Undetermined", status: "" },
            { key: "isp", label: "Service Provider", value: isp || "Undetermined", status: "" },
            { key: "dl", label: "Download Speed", value: live?.dlStatus ? `${show(live.dlStatus, live.testState === 1)} Mbps` : "", status: "" },
            { key: "ul", label: "Upload Speed", value: live?.ulStatus ? `${show(live.ulStatus, live.testState === 3)} Mbps` : "", status: "" },
            { key: "ping", label: "Ping", value: live?.pingStatus ? `${live.pingStatus} ms` : "", status: "" },
            { key: "jit", label: "Jitter", value: live?.jitterStatus ? `${live.jitterStatus} ms` : "", status: "" }
        ];
    };

    return (
        <div ref={captureRef} className="flex flex-col gap-2 px-0 sm:px-6 bg-transparent">
            <div className="text-center text-gray-600 text-xs font-normal font-sora leading-4">
                Tests are performed between your system and an ECPI server located in Richmond, VA. Please use Google Chrome for the most accurate results.
            </div>

            {/* One column below md: two ~140px columns squeezed the label/value/badge rows and the 320px CTA overflowed. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 items-start">
                <div className="flex flex-col gap-3 min-w-0">
                    <Section title="Browser Information">
                        {browserResults.map((r) => <ResultRow key={r.key} result={r} />)}
                        {webcamResult
                            ? <ResultRow result={webcamResult} />
                            : <ResultRow result={{ key: "cam", label: "Webcam Detected?", value: "", status: "" }} pending />}
                    </Section>

                    <Section title="System Information">
                        {systemResults.map((r) => <ResultRow key={r.key} result={r} />)}
                    </Section>
                </div>

                <div className="flex flex-col gap-3 min-w-0">
                    <Section title="Network Information">
                        {networkError && (
                            <div className="py-1 flex items-start gap-1.5 text-red-600 text-xs font-sora" role="alert">
                                <XCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" />{networkError}
                            </div>
                        )}
                        {networkResults
                            ? networkResults.map((r) => <ResultRow key={r.key} result={r} />)
                            : liveNetworkRows().map((r) => <ResultRow key={r.key} result={r} pending={running} />)}
                        {running && (
                            <div className="mt-2 w-full h-1.5 bg-sky-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={Math.round(progress * 100)} aria-valuemin={0} aria-valuemax={100}>
                                <div className="h-full bg-[#0053C6] transition-all duration-200" style={{ width: `${progress * 100}%` }} />
                            </div>
                        )}
                    </Section>

                    <div className="w-full flex flex-wrap justify-center items-center gap-4" data-capture-exclude="true">
                        {running
                            ? (
                                <FormButton type="button" intent="primary" shape="cta" className="max-w-full font-semibold font-sora text-sm" onClick={abortTest}>
                                    Abort Network Test
                                </FormButton>
                            )
                            : (
                                <FormButton type="button" intent="primary" shape="cta" className="max-w-full font-semibold font-sora text-sm" onClick={runNetworkTest} disabled={capturing}>
                                    Run Network Test Again
                                </FormButton>
                            )}
                        <a href="https://speedtest.ecpi.net/info.html" target="_blank" rel="noreferrer" className="text-[#0053C6] text-xs font-semibold font-sora underline">
                            View FAQ
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemCheckPanel;
